import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Copy, Check } from 'lucide-react';
import { api, API, pad } from '../../lib/api';

const Snippet = ({ label, code, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Copied');
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#060a14]/60 overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
        <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</p>
        <button onClick={copy} className="text-slate-500 hover:text-amber-300" aria-label="Copy" data-testid={`${testId}-copy`}>{copied ? <Check size={14} /> : <Copy size={14} />}</button>
      </div>
      <pre className="px-5 py-4 font-mono-precision text-[11.5px] leading-relaxed text-slate-300 overflow-x-auto whitespace-pre-wrap break-all">{code}</pre>
    </div>
  );
};

export default function ApiAccess({ docs }) {
  const [docId, setDocId] = useState(docs[0]?.id || '');
  const [token, setTokenState] = useState('');
  useEffect(() => { api.post('/admin/token').then((r) => setTokenState(r.data.token)).catch(() => {}); }, []);
  const doc = docs.find((d) => d.id === docId);

  return (
    <div className="space-y-6" data-testid="api-access-panel">
      <div>
        <h2 className="font-celestial text-2xl text-slate-100">Edit with another AI</h2>
        <p className="text-sm text-slate-400 mt-3 leading-relaxed max-w-2xl">
          Hand these to Claude, ChatGPT, Cursor or any agent that can run <code className="text-cyan-300">curl</code>. It can pull a doctrine's Markdown, rewrite it, and push it back — the layout on the site re-renders instantly. This token is issued fresh each time you open this tab and is valid for 30 days. Your browser session itself uses a secure httpOnly cookie.
        </p>
      </div>

      <label className="block max-w-md">
        <span className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">Doctrine</span>
        <select value={docId} onChange={(e) => setDocId(e.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-slate-100 outline-none" data-testid="api-doc-select">
          {docs.map((d) => <option key={d.id} value={d.id}>{pad(d.number)} — {d.title}</option>)}
        </select>
      </label>

      <Snippet label="Bearer token" code={token || ''} testId="api-token-snippet" />
      {doc && (
        <>
          <Snippet label="1 · Pull the current markdown" testId="api-pull-snippet" code={`curl -s "${API}/admin/doctrines/${doc.id}" \\\n  -H "Authorization: Bearer ${token}" | jq -r .body_md > "${doc.slug}.md"`} />
          <Snippet label="2 · Push edited markdown back (title detected from first # heading)" testId="api-push-snippet" code={`curl -s -X PUT "${API}/admin/doctrines/${doc.id}/markdown" \\\n  -H "Authorization: Bearer ${token}" \\\n  -H "Content-Type: text/markdown" \\\n  --data-binary @"${doc.slug}.md"`} />
          <Snippet label="3 · Update metadata (JSON, any subset of fields)" testId="api-meta-snippet" code={`curl -s -X PUT "${API}/admin/doctrines/${doc.id}" \\\n  -H "Authorization: Bearer ${token}" -H "Content-Type: application/json" \\\n  -d '{"subtitle":"…","abstract":"…","tags":["polarity","bloodline"],"status":"published"}'`} />
        </>
      )}
      <Snippet label="Public · read-only markdown of any published doctrine" testId="api-public-snippet" code={`curl -s "${API}/doctrines/<slug>/markdown"`} />
    </div>
  );
}
